import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { connectSocket, disconnectSocket, socket } from '../../services/socket';
import { formatChinaTime } from '../../utils/chinaTime';
import { syncPeerReviewDraft } from '../../utils/adventurePeerReviewDraft';
import { getAssetUrl } from '../teacher/teacherAdventureShared';

type AdventurePeerWorkType = 'danger' | 'escape';
type CommentMode = 'praise' | 'suggestion';
const DANGER_TAG_DELIMITER = '｜';

type AdventurePeerGalleryItem = {
  id: string;
  studentId: string;
  studentName: string;
  avatar?: string | null;
  sceneGroup?: string;
  sceneLabel?: string;
  sceneName?: string;
  isOwnWork?: boolean;
  imageUrl: string;
  thumbnailUrl: string;
  averageScore: number;
  reviewCount: number;
  likeCount?: number;
  hotComment?: string;
  tagCounts?: Array<{
    tag: string;
    count: number;
  }>;
  dimensionAverages: {
    dimensionOne: number;
    dimensionTwo: number;
    dimensionThree: number;
  };
  createdAt?: string | null;
};

type AdventurePeerGalleryResponse = {
  classroomId: string;
  items: AdventurePeerGalleryItem[];
};

type AdventurePeerWorkDetail = AdventurePeerGalleryItem & {
  classroomId: string;
  workType: AdventurePeerWorkType;
  dimensionAverages: {
    dimensionOne: number;
    dimensionTwo: number;
    dimensionThree: number;
  };
  canReview: boolean;
  viewerReview: {
    commentTemplate: string;
    dimensionOneStars: number;
    dimensionTwoStars: number;
    dimensionThreeStars: number;
  } | null;
};

type SocketSummaryPayload = {
  assignmentId: string;
  workType: AdventurePeerWorkType;
  workId: string;
  summary: {
    averageScore: number;
    reviewCount: number;
    likeCount?: number;
    hotComment?: string;
    tagCounts?: Array<{
      tag: string;
      count: number;
    }>;
    dimensionAverages: {
      dimensionOne: number;
      dimensionTwo: number;
      dimensionThree: number;
    };
  };
};

const GALLERY_CONFIG = {
  danger: {
    title: '同学的险情设计',
    detailLabel: (studentName: string) => `查看${studentName}的险情设计详情`,
    backLabel: '返回探险进度',
    emptyText: '同学们的险情设计还在陆续登场，稍后再来看一看。',
    submitLabel: '送出这个点赞理由',
    dimensions: [] as const,
    praiseTemplates: [
      '贴合险情',
      '办法机智',
      '容易做到',
      '很有创意',
      '很受启发',
    ],
    suggestionTemplates: [] as const,
  },
  escape: {
    title: '同学的逃生故事',
    detailLabel: (studentName: string) => `查看${studentName}的逃生故事详情`,
    backLabel: '返回探险进度',
    emptyText: '同学们的逃生故事还在更新，等会儿再来翻翻。',
    submitLabel: '送出这条短评',
    dimensions: [
      { key: 'dimensionOneStars', label: '想象丰富合理' },
      { key: 'dimensionTwoStars', label: '困境描写具体' },
      { key: 'dimensionThreeStars', label: '求生方法具体' },
    ] as const,
    praiseTemplates: [
      '这一段写得很有画面感。',
      '原来还可以这样脱险！',
      '这个队友选得真有用！',
      '我读到这里也跟着紧张起来了！',
      '好神奇的一段经历！',
      '我感觉自己也在现场！',
    ],
    suggestionTemplates: [
      '这里可以写写你当时的心情。',
      '险情来临的时刻好像不太清楚。',
      '这里要是再惊险一点就更好了！',
      '逃生过程似乎有点简略。',
    ],
  },
} as const;

const POLL_INTERVAL_MS = 15000;
const COMMENT_TEMPLATE_BATCH_SIZE = 3;
const DANGER_SCENE_GROUPS = [
  { key: 'desert', label: '茫茫大漠' },
  { key: 'rainforest', label: '热带雨林' },
  { key: 'island', label: '海中荒岛' },
  { key: 'cave', label: '幽深洞穴' },
  { key: 'glacier', label: '冰川' },
  { key: 'custom', label: '自定义场景' },
] as const;

const isAdventurePeerWorkType = (value: string | undefined): value is AdventurePeerWorkType =>
  value === 'danger' || value === 'escape';

const formatAverageScore = (value: number) => value.toFixed(1);

const getTemplateBatch = (templates: string[], batchIndex: number) => {
  if (templates.length <= COMMENT_TEMPLATE_BATCH_SIZE) {
    return templates;
  }

  const startIndex = (batchIndex * COMMENT_TEMPLATE_BATCH_SIZE) % templates.length;
  const batch: string[] = [];
  for (let offset = 0; offset < COMMENT_TEMPLATE_BATCH_SIZE; offset += 1) {
    const item = templates[(startIndex + offset) % templates.length];
    if (item) {
      batch.push(item);
    }
  }

  return batch;
};

const splitDangerCommentTemplates = (value: string) =>
  value
    .split(DANGER_TAG_DELIMITER)
    .map((item) => item.trim())
    .filter(Boolean);

const joinDangerCommentTemplates = (values: string[]) =>
  values.map((item) => item.trim()).filter(Boolean).join(DANGER_TAG_DELIMITER);

const normalizeViewerRotation = (rotation: number) => ((rotation % 360) + 360) % 360;
const normalizeViewerScale = (scale: number) => {
  const rounded = Math.round(scale * 100) / 100;
  return Math.min(2.5, Math.max(1, rounded));
};
const buildViewerTransform = (rotation: number, scale = 1) =>
  scale === 1 ? `rotate(${rotation}deg)` : `rotate(${rotation}deg) scale(${scale})`;

const RotationActionButton: React.FC<{
  label: string;
  onClick: () => void;
  icon: 'left' | 'right' | 'reset' | 'zoomIn' | 'zoomOut' | 'zoomReset';
}> = ({ label, onClick, icon }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/80 bg-white/92 text-slate-700 shadow-lg backdrop-blur transition hover:scale-[1.03] hover:border-slate-300 hover:text-slate-900"
  >
    {icon === 'left' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M8 7L3 12l5 5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 12h9a7 7 0 0 1 7 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : icon === 'right' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M16 7l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 12h-9a7 7 0 0 0-7 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : icon === 'zoomIn' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="11" cy="11" r="6" />
        <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
        <path d="M11 8v6M8 11h6" strokeLinecap="round" />
      </svg>
    ) : icon === 'zoomOut' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="11" cy="11" r="6" />
        <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
        <path d="M8 11h6" strokeLinecap="round" />
      </svg>
    ) : icon === 'zoomReset' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="11" cy="11" r="6" />
        <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
        <path d="M8.5 8.5l5 5M13.5 8.5l-5 5" strokeLinecap="round" />
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M20 11a8 8 0 1 0 2 5.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 4v7h-7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);

const RotationToolbar: React.FC<{
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onReset: () => void;
  className?: string;
}> = ({ onRotateLeft, onRotateRight, onReset, className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <RotationActionButton label="向左旋转" onClick={onRotateLeft} icon="left" />
    <RotationActionButton label="回正" onClick={onReset} icon="reset" />
    <RotationActionButton label="向右旋转" onClick={onRotateRight} icon="right" />
  </div>
);

const ZoomToolbar: React.FC<{
  onZoomOut: () => void;
  onZoomIn: () => void;
  onReset: () => void;
  className?: string;
}> = ({ onZoomOut, onZoomIn, onReset, className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <RotationActionButton label="缩小图片" onClick={onZoomOut} icon="zoomOut" />
    <RotationActionButton label="重置缩放" onClick={onReset} icon="zoomReset" />
    <RotationActionButton label="放大图片" onClick={onZoomIn} icon="zoomIn" />
  </div>
);

const mergeCardSummary = (
  items: AdventurePeerGalleryItem[],
  payload: SocketSummaryPayload
) =>
  items.map((item) =>
    item.id === payload.workId
      ? {
        ...item,
        averageScore: payload.summary.averageScore,
        reviewCount: payload.summary.reviewCount,
        likeCount: payload.summary.likeCount ?? item.likeCount,
        hotComment: payload.summary.hotComment ?? item.hotComment,
        tagCounts: payload.summary.tagCounts ?? item.tagCounts,
        dimensionAverages: payload.summary.dimensionAverages,
      }
      : item
  );

const renderDimensionSummary = (
  dimensions: ReadonlyArray<{ key: string; label: string }>,
  averages: AdventurePeerGalleryItem['dimensionAverages']
) => (
  <div className="mt-3 grid gap-2 sm:grid-cols-3">
    {dimensions.map((dimension, index) => {
      const value =
        index === 0
          ? averages.dimensionOne
          : index === 1
            ? averages.dimensionTwo
            : averages.dimensionThree;

      return (
        <div key={dimension.key} className="rounded-2xl bg-slate-50 px-3 py-3 text-sm text-slate-600">
          <div className="font-semibold text-slate-700">{dimension.label}</div>
          <div className="mt-1 text-base font-black text-amber-600">{formatAverageScore(value)}</div>
        </div>
      );
    })}
  </div>
);

const PeerWorkImage: React.FC<{
  src?: string;
  alt: string;
  className: string;
  fallbackClassName: string;
  fallbackLabel?: string;
  style?: React.CSSProperties;
}> = ({ src, alt, className, fallbackClassName, fallbackLabel, style }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (!src || hasError) {
    return (
      <div className={fallbackClassName}>
        <div className="text-4xl text-slate-300">🖼️</div>
        {fallbackLabel ? <p className="text-sm text-slate-500">{fallbackLabel}</p> : null}
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} style={style} onError={() => setHasError(true)} />;
};

export const AdventurePeerGalleryPage: React.FC = () => {
  const { assignmentId, workType: workTypeParam } = useParams<{
    assignmentId: string;
    workType: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  const workType = isAdventurePeerWorkType(workTypeParam) ? workTypeParam : 'danger';
  const config = GALLERY_CONFIG[workType];
  const fallbackReturnTo = assignmentId ? `/student/adventure/${assignmentId}` : '/student';
  const returnTo =
    typeof (location.state as { returnTo?: unknown } | null)?.returnTo === 'string'
      ? ((location.state as { returnTo: string }).returnTo || fallbackReturnTo)
      : fallbackReturnTo;
  const returnState =
    (() => {
      const candidate = (location.state as { returnState?: unknown } | null)?.returnState;
      return candidate && typeof candidate === 'object' ? candidate : null;
    })();

  const [gallery, setGallery] = useState<AdventurePeerGalleryResponse>({ classroomId: '', items: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedWorkId, setSelectedWorkId] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<AdventurePeerWorkDetail | null>(null);
  const [selectedDetailRotation, setSelectedDetailRotation] = useState(0);
  const [selectedDetailScale, setSelectedDetailScale] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [selectedRatings, setSelectedRatings] = useState({
    dimensionOneStars: 0,
    dimensionTwoStars: 0,
    dimensionThreeStars: 0,
  });
  const [activeCommentMode, setActiveCommentMode] = useState<CommentMode>('praise');
  const [commentBatchIndex, setCommentBatchIndex] = useState(0);
  const [selectedCommentTemplate, setSelectedCommentTemplate] = useState('');
  const [selectedDangerCommentTemplates, setSelectedDangerCommentTemplates] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const reviewDraftRef = useRef({
    dimensionOneStars: 0,
    dimensionTwoStars: 0,
    dimensionThreeStars: 0,
    commentTemplate: '',
  });
  const preferredSceneGroup =
    typeof (location.state as { preferredSceneGroup?: unknown } | null)?.preferredSceneGroup === 'string'
      ? ((location.state as { preferredSceneGroup: string }).preferredSceneGroup || '')
      : '';
  const [selectedSceneGroup, setSelectedSceneGroup] = useState(preferredSceneGroup);

  const loadGallery = async (options?: { silent?: boolean }) => {
    if (!assignmentId) {
      return;
    }

    if (!options?.silent) {
      setIsLoading(true);
      setErrorMessage('');
    }

    try {
      const response = await api.get<AdventurePeerGalleryResponse>('/submissions/adventure-peer-gallery', {
        params: {
          assignmentId,
          workType,
        },
      });
      setGallery(response.data);
    } catch (error) {
      console.error('Failed to load adventure peer gallery', error);
      setErrorMessage('同学作品广场暂时没有打开，请稍后再试。');
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  };

  const loadDetail = async (workId: string, options?: { silent?: boolean }) => {
    if (!assignmentId) {
      return;
    }

    if (!options?.silent) {
      setIsDetailLoading(true);
      setDetailError('');
    }

    try {
      const response = await api.get<AdventurePeerWorkDetail>(
        `/submissions/adventure-peer-gallery/${workId}`,
        {
          params: {
            assignmentId,
            workType,
          },
        }
      );
      setSelectedDetail(response.data);
      const nextDraft = syncPeerReviewDraft({
        currentDraft: reviewDraftRef.current,
        viewerReview: response.data.viewerReview,
        silent: options?.silent ?? false,
      });
      setSelectedRatings({
        dimensionOneStars: nextDraft.dimensionOneStars,
        dimensionTwoStars: nextDraft.dimensionTwoStars,
        dimensionThreeStars: nextDraft.dimensionThreeStars,
      });
      setSelectedCommentTemplate(nextDraft.commentTemplate);
      setSelectedDangerCommentTemplates(
        workType === 'danger' ? splitDangerCommentTemplates(nextDraft.commentTemplate) : []
      );
    } catch (error) {
      console.error('Failed to load adventure peer detail', error);
      setDetailError('这份作品刚刚闪了一下神，再点一次试试。');
    } finally {
      if (!options?.silent) {
        setIsDetailLoading(false);
      }
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadGallery({ silent: true });
      if (selectedWorkId) {
        await loadDetail(selectedWorkId, { silent: true });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    void loadGallery();
  }, [assignmentId, workType]);

  useEffect(() => {
    if (workType !== 'danger' || !gallery.items.length) {
      return;
    }

    if (selectedSceneGroup) {
      return;
    }

    const ownWork = gallery.items.find((item) => item.isOwnWork);
    setSelectedSceneGroup(ownWork?.sceneGroup || preferredSceneGroup || gallery.items[0]?.sceneGroup || 'custom');
  }, [gallery.items, preferredSceneGroup, selectedSceneGroup, workType]);

  const effectiveDangerSceneGroup =
    selectedSceneGroup ||
    gallery.items.find((item) => item.isOwnWork)?.sceneGroup ||
    preferredSceneGroup ||
    gallery.items[0]?.sceneGroup ||
    'custom';

  useEffect(() => {
    reviewDraftRef.current = {
      ...selectedRatings,
      commentTemplate:
        workType === 'danger'
          ? joinDangerCommentTemplates(selectedDangerCommentTemplates)
          : selectedCommentTemplate,
    };
  }, [selectedCommentTemplate, selectedDangerCommentTemplates, selectedRatings, workType]);

  useEffect(() => {
    if (!selectedWorkId) {
      setSelectedDetail(null);
      setSelectedDetailRotation(0);
      setSelectedRatings({
        dimensionOneStars: 0,
        dimensionTwoStars: 0,
        dimensionThreeStars: 0,
      });
      setActiveCommentMode('praise');
      setCommentBatchIndex(0);
      setSelectedCommentTemplate('');
      setSelectedDangerCommentTemplates([]);
      return;
    }

    void loadDetail(selectedWorkId);
  }, [selectedWorkId, assignmentId, workType]);

  useEffect(() => {
    if (!gallery.classroomId || !assignmentId) {
      return;
    }

    connectSocket();
    socket.emit('join_classroom', gallery.classroomId);

    const handleSummaryUpdate = (payload: SocketSummaryPayload) => {
      if (payload.assignmentId !== assignmentId || payload.workType !== workType) {
        return;
      }

      setGallery((prev) => ({
        ...prev,
        items: mergeCardSummary(prev.items, payload),
      }));

      setSelectedDetail((prev) =>
        prev && prev.id === payload.workId
          ? {
            ...prev,
            averageScore: payload.summary.averageScore,
            reviewCount: payload.summary.reviewCount,
            likeCount: payload.summary.likeCount ?? prev.likeCount,
            hotComment: payload.summary.hotComment ?? prev.hotComment,
            tagCounts: payload.summary.tagCounts ?? prev.tagCounts,
            dimensionAverages: payload.summary.dimensionAverages,
          }
          : prev
      );
    };

    socket.on('adventure_peer_review_updated', handleSummaryUpdate);

    return () => {
      socket.off('adventure_peer_review_updated', handleSummaryUpdate);
      disconnectSocket();
    };
  }, [assignmentId, gallery.classroomId, workType]);

  useEffect(() => {
    if (!assignmentId) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadGallery({ silent: true });
      if (selectedWorkId) {
        void loadDetail(selectedWorkId, { silent: true });
      }
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [assignmentId, selectedWorkId, workType]);

  const submitDisabled = useMemo(
    () =>
      !selectedDetail?.canReview ||
      isSubmitting ||
      !(workType === 'danger' ? selectedDangerCommentTemplates.length > 0 : selectedCommentTemplate) ||
      Object.values(selectedRatings).some((value) => value < 1),
    [isSubmitting, selectedCommentTemplate, selectedDangerCommentTemplates.length, selectedDetail?.canReview, selectedRatings, workType]
  );

  const visibleItems = useMemo(() => {
    if (workType === 'danger') {
      const ownWork = gallery.items.find((item) => item.isOwnWork) ?? null;
      const otherItems = [...gallery.items]
        .filter((item) => !item.isOwnWork && (item.sceneGroup || 'custom') === effectiveDangerSceneGroup)
        .sort((left, right) => (right.createdAt || '').localeCompare(left.createdAt || ''));

      return ownWork ? [ownWork, ...otherItems] : otherItems;
    }

    return [...gallery.items].sort((left, right) => {
      if (left.isOwnWork && !right.isOwnWork) return -1;
      if (!left.isOwnWork && right.isOwnWork) return 1;
      return (right.createdAt || '').localeCompare(left.createdAt || '');
    });
  }, [effectiveDangerSceneGroup, gallery.items, workType]);

  const dangerSceneCounts = useMemo(() => {
    const counts = new Map<string, number>();
    DANGER_SCENE_GROUPS.forEach((scene) => counts.set(scene.key, 0));
    gallery.items.forEach((item) => {
      const key = item.sceneGroup || 'custom';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return counts;
  }, [gallery.items]);

  const visibleCommentTemplates = useMemo(() => {
    if (workType === 'danger') {
      return [...config.praiseTemplates];
    }

    return getTemplateBatch(
      activeCommentMode === 'praise' ? [...config.praiseTemplates] : [...config.suggestionTemplates],
      commentBatchIndex
    );
  }, [activeCommentMode, commentBatchIndex, config.praiseTemplates, config.suggestionTemplates, workType]);

  const handleSubmitReview = async () => {
    if (!assignmentId || !selectedDetail || submitDisabled) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post<AdventurePeerWorkDetail>(
        `/submissions/adventure-peer-gallery/${selectedDetail.id}/reviews`,
        {
          assignmentId,
          workType,
          ...(workType === 'danger'
            ? {
              dimensionOneStars: 5,
              dimensionTwoStars: 5,
              dimensionThreeStars: 5,
            }
            : selectedRatings),
          commentTemplate:
            workType === 'danger'
              ? joinDangerCommentTemplates(selectedDangerCommentTemplates)
              : selectedCommentTemplate,
        }
      );

      setSelectedDetail(response.data);
      setGallery((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.id === response.data.id
            ? {
              ...item,
              averageScore: response.data.averageScore,
              reviewCount: response.data.reviewCount,
              likeCount: response.data.likeCount,
              hotComment: response.data.hotComment,
              tagCounts: response.data.tagCounts,
              dimensionAverages: response.data.dimensionAverages,
            }
            : item
        ),
      }));
    } catch (error) {
      console.error('Failed to submit adventure peer review', error);
      setDetailError('这条点评还没送出去，请稍后再试。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#fffbe8_0%,#f8fafc_40%,#eef6ff_100%)] px-4 py-6 md:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-4 rounded-[2rem] border border-white/70 bg-white/85 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-amber-500">同学作品广场</p>
            <h1 className="mt-2 text-3xl font-black text-slate-900">{config.title}</h1>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-600">
              {workType === 'danger'
                ? '你的作品会固定在最上面，下面再看同学们的新作品，也可以切去其他场景找灵感。'
                : '看看全班的逃生故事，再送出你的星级和一句短评。'}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate(returnTo, { state: returnState ?? undefined })}
              className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
            >
              {config.backLabel}
            </button>
            <button
              type="button"
              onClick={() => {
                void handleRefresh();
              }}
              disabled={isRefreshing}
              className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isRefreshing ? '刷新中...' : '刷新作品'}
            </button>
          </div>
        </div>

        {errorMessage ? (
          <div className="rounded-[1.6rem] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
            {errorMessage}
          </div>
        ) : null}

        <section className="space-y-4">
          {workType === 'danger' ? (
            <div className="flex flex-wrap gap-2">
              {DANGER_SCENE_GROUPS.map((scene) => (
                <button
                  key={scene.key}
                  type="button"
                  onClick={() => setSelectedSceneGroup(scene.key)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${effectiveDangerSceneGroup === scene.key
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                    }`}
                >
                  <span>{scene.label}</span>
                  <span className={`ml-2 text-xs ${effectiveDangerSceneGroup === scene.key ? 'text-white/80' : 'text-slate-400'}`}>
                    {dangerSceneCounts.get(scene.key) ?? 0}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {isLoading ? (
            <div className="rounded-[1.8rem] border border-dashed border-slate-200 bg-white/70 px-6 py-12 text-center text-sm text-slate-500">
              正在打开同学作品广场...
            </div>
          ) : gallery.items.length === 0 ? (
            <div className="rounded-[1.8rem] border border-dashed border-slate-200 bg-white/70 px-6 py-12 text-center text-sm text-slate-500">
              {config.emptyText}
            </div>
          ) : workType === 'danger' && visibleItems.length === 0 ? (
            <div className="rounded-[1.8rem] border border-dashed border-slate-200 bg-white/70 px-6 py-12 text-center text-sm text-slate-500">
              这个场景下暂时还没有同学上传作品。
            </div>
          ) : (
            visibleItems.map((item) => (
              <article
                key={item.id}
                className="overflow-hidden rounded-[1.8rem] border border-white/70 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)]"
              >
                <div className="grid gap-4 p-4 md:grid-cols-[220px_minmax(0,1fr)]">
                  <div className="overflow-hidden rounded-[1.4rem] bg-slate-100">
                    <PeerWorkImage
                      src={getAssetUrl(item.thumbnailUrl)}
                      alt={`${item.studentName} 的作品缩略图`}
                      className="h-52 w-full object-cover"
                      fallbackClassName="flex h-52 flex-col items-center justify-center gap-3 px-4 text-center"
                      fallbackLabel="这张作品图暂时无法显示"
                    />
                  </div>
                  <div className="flex flex-col justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-lg text-amber-700">
                          {item.avatar || item.studentName.slice(0, 1)}
                        </div>
                        <div>
                          <p className="text-lg font-black text-slate-900">{item.studentName}</p>
                          <p className="text-sm text-slate-500">
                            {workType === 'danger'
                              ? `${item.likeCount ?? item.reviewCount}个赞`
                              : item.createdAt
                                ? `上传于 ${formatChinaTime(item.createdAt)}`
                                : ''}
                          </p>
                        </div>
                      </div>
                      {item.isOwnWork ? (
                        <span className="mt-3 inline-flex rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                          我的
                        </span>
                      ) : null}
                      {workType === 'danger' ? null : renderDimensionSummary(config.dimensions, item.dimensionAverages)}
                      {item.hotComment ? (
                        <p className="mt-3 rounded-2xl bg-amber-50 px-4 py-3 text-sm leading-7 text-amber-900">
                          {item.hotComment}
                        </p>
                      ) : (
                        <p className="mt-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm leading-7 text-slate-500">
                          第一条短评还在路上。
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedWorkId(item.id);
                        setSelectedDetailRotation(0);
                        setSelectedDetailScale(1);
                      }}
                      className="inline-flex w-fit items-center rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
                      aria-label={config.detailLabel(item.studentName)}
                    >
                      查看这份作品
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </section>

        {selectedWorkId && (
          <div
            className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/55 px-4 py-6"
            onClick={() => {
              setSelectedWorkId(null);
              setSelectedDetail(null);
              setSelectedDetailRotation(0);
              setSelectedDetailScale(1);
              setDetailError('');
            }}
          >
            <div
              role="dialog"
              aria-label={config.detailLabel(selectedDetail?.studentName ?? '同学')}
              className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl mx-auto"
              onClick={(event) => event.stopPropagation()}
            >
              {!selectedDetail || isDetailLoading ? (
                <div className="flex min-h-[420px] items-center justify-center rounded-[1.4rem] border border-dashed border-slate-200 bg-slate-50 px-6 text-center text-sm text-slate-500">
                  正在打开这份作品...
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-lg text-amber-700">
                        {selectedDetail.avatar || selectedDetail.studentName.slice(0, 1)}
                      </div>
                      <div>
                        <p className="text-lg font-black text-slate-900">{selectedDetail.studentName}</p>
                        <p className="text-sm text-slate-500">
                          {workType === 'danger'
                            ? `${selectedDetail.likeCount ?? selectedDetail.reviewCount}个赞`
                            : selectedDetail.createdAt
                              ? `上传于 ${formatChinaTime(selectedDetail.createdAt)}`
                              : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedWorkId(null);
                        setSelectedDetail(null);
                        setSelectedDetailRotation(0);
                        setSelectedDetailScale(1);
                        setDetailError('');
                      }}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      关闭
                    </button>
                  </div>

                  <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.8fr)]">
                    <div className="space-y-4">
                      <div className="relative overflow-hidden rounded-[1.75rem] bg-stone-100">
                        <ZoomToolbar
                          className="absolute left-4 top-4 z-10"
                          onZoomOut={() => setSelectedDetailScale((prev) => normalizeViewerScale(prev - 0.25))}
                          onZoomIn={() =>
                            setSelectedDetailScale((prev) => normalizeViewerScale(prev >= 1.25 ? prev + 0.25 : 1.25))
                          }
                          onReset={() => setSelectedDetailScale(1)}
                        />
                        <RotationToolbar
                          className="absolute right-4 top-4 z-10"
                          onRotateLeft={() =>
                            setSelectedDetailRotation((prev) => normalizeViewerRotation(prev - 90))
                          }
                          onRotateRight={() =>
                            setSelectedDetailRotation((prev) => normalizeViewerRotation(prev + 90))
                          }
                          onReset={() => setSelectedDetailRotation(0)}
                        />
                        <PeerWorkImage
                          src={getAssetUrl(selectedDetail.imageUrl)}
                          alt={`${selectedDetail.studentName} 的作品大图`}
                          className="h-full max-h-[70vh] w-full object-contain transition-transform"
                          fallbackClassName="flex h-80 flex-col items-center justify-center gap-3 px-6 text-center"
                          fallbackLabel="这张作品图暂时无法显示"
                          style={{
                            transform: buildViewerTransform(selectedDetailRotation, selectedDetailScale),
                          }}
                        />
                      </div>
                      {workType === 'danger' && selectedDetail.tagCounts?.length ? (
                        <div className="rounded-[1.4rem] bg-amber-50 px-4 py-4 text-amber-900">
                          <p className="text-sm font-semibold">大家常选这些理由</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {selectedDetail.tagCounts.slice(0, 3).map((item) => (
                              <span
                                key={`highlight-${item.tag}`}
                                className="rounded-full bg-white px-3 py-2 text-sm font-semibold text-amber-900"
                              >
                                {item.tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : selectedDetail.hotComment ? (
                        <div className="rounded-[1.4rem] bg-amber-50 px-4 py-4 text-sm leading-7 text-amber-900">
                          {selectedDetail.hotComment}
                        </div>
                      ) : null}
                      {workType === 'danger' ? (
                        selectedDetail.tagCounts?.length ? (
                          <div className="flex flex-wrap gap-2">
                            {selectedDetail.tagCounts.map((item) => (
                              <span
                                key={item.tag}
                                className="rounded-full bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700"
                              >
                                {item.tag} {item.count}
                              </span>
                            ))}
                          </div>
                        ) : null
                      ) : (
                        renderDimensionSummary(config.dimensions, selectedDetail.dimensionAverages)
                      )}
                    </div>

                    <div className="space-y-5">
                      {detailError ? (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                          {detailError}
                        </div>
                      ) : null}

                      {selectedDetail.canReview ? (
                        <div className="space-y-5">
                          {workType === 'danger' ? (
                            <div className="rounded-[1.4rem] border border-slate-200 bg-slate-50 p-4">
                              <p className="text-sm font-semibold text-slate-700">1. 给你觉得好的方法点个赞</p>
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedRatings({
                                    dimensionOneStars: 5,
                                    dimensionTwoStars: 5,
                                    dimensionThreeStars: 5,
                                  })
                                }
                                className={`mt-3 rounded-full px-4 py-2 text-sm font-semibold transition ${selectedRatings.dimensionOneStars > 0
                                  ? 'bg-amber-500 text-white'
                                  : 'border border-slate-200 bg-white text-slate-700'
                                  }`}
                              >
                                点个赞
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {config.dimensions.map((dimension) => {
                                const currentValue = selectedRatings[dimension.key];
                                return (
                                  <div key={dimension.key}>
                                    <p className="text-sm font-semibold text-slate-700">{dimension.label}</p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {[1, 2, 3, 4, 5].map((value) => (
                                        <button
                                          key={`${dimension.key}-${value}`}
                                          type="button"
                                          onClick={() =>
                                            setSelectedRatings((prev) => ({
                                              ...prev,
                                              [dimension.key]: value,
                                            }))
                                          }
                                          aria-label={`${dimension.label} ${value} 星`}
                                          className={`rounded-full border px-3 py-2 text-sm font-semibold transition ${currentValue === value
                                            ? 'border-amber-500 bg-amber-500 text-white'
                                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                                            }`}
                                        >
                                          {value} 星
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div className="space-y-4">
                            <div className="rounded-[1.4rem] border border-slate-200 bg-slate-50 p-4">
                              <div className="grid grid-cols-2 gap-3">
                                {workType === 'danger'
                                  ? null
                                  : (
                                    [
                                      ['praise', '我想夸夸这个想法'],
                                      ['suggestion', '我还有一些小建议'],
                                    ] as const
                                  ).map(([mode, label]) => (
                                    <button
                                      key={mode}
                                      type="button"
                                      onClick={() => {
                                        setActiveCommentMode(mode);
                                        setCommentBatchIndex(0);
                                      }}
                                      className={`rounded-full px-4 py-3 text-sm font-semibold transition ${activeCommentMode === mode
                                        ? 'bg-slate-900 text-white'
                                        : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                                        }`}
                                    >
                                      {label}
                                    </button>
                                  ))}
                              </div>

                              <p className="mt-3 text-sm font-semibold text-slate-700">
                                {workType === 'danger' ? '2. 好在哪儿？' : ''}
                              </p>

                              <div className="mt-4 flex flex-wrap gap-2">
                                {visibleCommentTemplates.map((template) => (
                                  <button
                                    key={template}
                                    type="button"
                                    onClick={() => {
                                      if (workType === 'danger') {
                                        setSelectedDangerCommentTemplates((prev) =>
                                          prev.includes(template)
                                            ? prev.filter((item) => item !== template)
                                            : [...prev, template]
                                        );
                                        return;
                                      }

                                      setSelectedCommentTemplate(template);
                                    }}
                                    aria-label={`选择短评：${template}`}
                                    className={`rounded-2xl border px-3 py-2 text-left text-sm leading-6 transition ${(workType === 'danger'
                                      ? selectedDangerCommentTemplates.includes(template)
                                      : selectedCommentTemplate === template)
                                      ? 'border-sky-500 bg-sky-500 text-white'
                                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                                      }`}
                                  >
                                    {template}
                                  </button>
                                ))}
                              </div>

                              {workType !== 'danger' &&
                                (activeCommentMode === 'praise'
                                  ? config.praiseTemplates.length
                                  : config.suggestionTemplates.length) > COMMENT_TEMPLATE_BATCH_SIZE ? (
                                <button
                                  type="button"
                                  onClick={() => setCommentBatchIndex((prev) => prev + 1)}
                                  className="mt-4 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-100"
                                >
                                  换一组短评
                                </button>
                              ) : null}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={handleSubmitReview}
                            disabled={submitDisabled}
                            className="w-full rounded-[1.2rem] bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                          >
                            {config.submitLabel}
                          </button>
                        </div>
                      ) : selectedDetail.viewerReview ? (
                        <div className="space-y-4">
                          <div className="rounded-[1.4rem] border border-emerald-200 bg-emerald-50 px-4 py-4">
                            <p className="text-sm font-semibold text-emerald-700">
                              {selectedDetail.isOwnWork ? '同学正在这样看你的方法' : '你已送出点评'}
                            </p>
                            {workType !== 'danger' ? (
                              <p className="mt-2 text-sm leading-7 text-emerald-900">
                                {selectedDetail.viewerReview.commentTemplate}
                              </p>
                            ) : null}
                            {workType === 'danger' ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {splitDangerCommentTemplates(selectedDetail.viewerReview.commentTemplate).map((tag) => (
                                  <span
                                    key={tag}
                                    className="rounded-full bg-white px-3 py-2 text-sm font-semibold text-emerald-800"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="rounded-[1.4rem] border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-7 text-slate-500">
                            {selectedDetail.isOwnWork ? '这份是你的作品，这里展示同学们给出的点赞和理由。' : '这份作品暂时不能再点评了。'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
